import React from "react";
import { useState } from "react";
import type { SyntheticEvent } from "react";
import { Link, NavLink } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  Tooltip,
  MenuItem,
  Switch,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { useConfiguration } from "contexts/ConfigurationProvider/ConfigurationProvider";
import type { FeatureFlag } from "contexts/ConfigurationProvider/FeatureFlag";
import { FeatureFlags } from "contexts/ConfigurationProvider/FeatureFlags";
import useUser from "hooks/useUser";
import { envConfig } from "utils/envConfig";
import { useThemePalette, withTheme } from "components/Theme";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import type { RbacPolicy } from "components/authorization/RbacPolicies";
import { OctopusMangerLogo } from "components/icons/OctopusManagerLogo";
import { billingAccountRoutes } from "areas/billing-accounts/billing-account-routes";
import { useGuardedNavigate } from "areas/orders/hooks/useUnsavedChangesGuard";
import { orderRoutes } from "areas/orders/order-routes";
import { rateCardRoutes } from "areas/rate-cards/rate-card-routes";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";
import { UserRoutesMap } from "areas/users/UserRoutesMap";

interface Page {
  name: string;
  path: string;
  policies?: Array<RbacPolicy>;
  featureFlag?: FeatureFlag;
  exactMatch: boolean;
}

interface Setting {
  name: string;
  path: string;
}

interface Setting {
  name: string;
  path: string;
}

const pages: Page[] = [
  {
    name: "Subscriptions",
    path: subscriptionRoutes.root,
    policies: [RbacPolicies.Subscriptions.Read],
    exactMatch: false,
  },
  { name: "Users", path: UserRoutesMap.root, policies: [RbacPolicies.Users.Read], exactMatch: false },
  {
    name: "Rate Cards",
    path: rateCardRoutes.root,
    policies: [RbacPolicies.RateCards.Read],
    featureFlag: FeatureFlags.AdoptionLedPricing,
    exactMatch: false,
  },
  {
    name: "Billing Accounts",
    path: billingAccountRoutes.root,
    policies: [RbacPolicies.BillingAccounts.Read],
    featureFlag: FeatureFlags.AdoptionLedPricing,
    exactMatch: false,
  },
  {
    name: "Orders",
    path: orderRoutes.root,
    policies: [RbacPolicies.Orders.Read],
    featureFlag: FeatureFlags.AdoptionLedPricing,
    exactMatch: false,
  },
];

const settings: Setting[] = [{ name: "Sign out", path: "/sign-out" }];

const NavigationBar = () => {
  const { user } = useUser();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [palette, onChangePalette] = useThemePalette();
  const { isFeatureFlagEnabled } = useConfiguration();
  const navigateThroughGuard = useGuardedNavigate();

  const visiblePages: Page[] = pages.filter((page) => !page.featureFlag || isFeatureFlagEnabled(page.featureFlag));

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const togglePalette = (e: SyntheticEvent) => {
    e.preventDefault();
    onChangePalette(palette === "dark" ? "light" : "dark");
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstPage = pages[0]!;

  const versionHref =
    envConfig.Environment !== "Local"
      ? `https://github.com/OctopusDeploy/Commercial-Billing/releases/tag/${envConfig.AppVersion}`
      : `https://github.com/OctopusDeploy/Commercial-Billing`;

  return withTheme((theme) => {
    return (
      <AppBar position="static">
        <Container maxWidth={false}>
          <Toolbar disableGutters>
            <Box
              sx={{
                ml: -3,
                px: 3,
                backgroundColor: theme.navBackground,
                display: { xs: "none", md: "flex" },
                alignSelf: "stretch",
                alignItems: "center",
              }}
            >
              <OctopusMangerLogo fontSize="large" />
            </Box>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignSelf: "stretch",
                backgroundColor: theme.transparent,
              }}
            >
              <PolicyAuthorized policies={firstPage.policies}>
                <NavLink
                  to={firstPage.path}
                  onClick={navigateThroughGuard(firstPage.path)}
                  style={({ isActive }) => ({
                    display: "flex",
                    justifyItems: "center",
                    backgroundColor: isActive ? theme.navItemActive : theme.transparent,
                  })}
                >
                  <Button
                    onClick={handleCloseNavMenu}
                    sx={{
                      px: 3,
                      color: theme.navText,
                      display: "flex",
                      alignSelf: "stretch",
                      borderRadius: 0,
                      backgroundColor: theme.transparent,
                    }}
                  >
                    {firstPage.name}
                  </Button>
                </NavLink>
              </PolicyAuthorized>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block", md: "none" },
                }}
              >
                {visiblePages.map((page) => (
                  <PolicyAuthorized key={page.name} policies={page.policies}>
                    <MenuItem onClick={handleCloseNavMenu}>
                      <Link to={page.path} onClick={navigateThroughGuard(page.path)}>
                        <Typography
                          sx={{
                            textAlign: "center",
                          }}
                        >
                          {page.name}
                        </Typography>
                      </Link>
                    </MenuItem>
                  </PolicyAuthorized>
                ))}
              </Menu>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                width: "fit-content",
                display: { xs: "none", md: "flex" },
                alignSelf: "stretch",
              }}
            >
              {visiblePages.slice(1).map((page) => (
                <PolicyAuthorized key={page.name} policies={page.policies}>
                  <NavLink
                    to={page.path}
                    onClick={navigateThroughGuard(page.path)}
                    style={({ isActive }) => ({
                      display: "flex",
                      backgroundColor: isActive ? theme.navItemActive : theme.transparent,
                    })}
                  >
                    <Button
                      onClick={handleCloseNavMenu}
                      sx={{
                        px: 3,
                        color: theme.navText,
                        display: "block",
                        alignSelf: "stretch",
                        borderRadius: 0,
                        backgroundColor: theme.transparent,
                      }}
                    >
                      {page.name}
                    </Button>
                  </NavLink>
                </PolicyAuthorized>
              ))}
            </Box>
            <Box sx={{ flexGrow: 0, width: "fit-content", px: 1 }}>
              <Tooltip title="See changes in GitHub">
                <a href={versionHref} target="_blank" rel="noreferrer">
                  <Typography sx={{ color: theme.navText }}>Version {envConfig.AppVersion}</Typography>
                </a>
              </Tooltip>
            </Box>

            <Box sx={{ flexGrow: 0, display: "flex", alignItems: "stretch", alignSelf: "stretch", mr: -3 }}>
              <Tooltip title="Open settings">
                <Button variant={"text"} onClick={handleOpenUserMenu} sx={{ px: 2, color: theme.navText }}>
                  {user?.name} <ArrowDropDownIcon sx={{ ml: 1 }} />
                </Button>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={togglePalette}>
                  <FormGroup>
                    <FormControlLabel control={<Switch checked={palette === "dark"} />} label="Dark mode" />
                  </FormGroup>
                </MenuItem>

                {settings.map((setting) => (
                  <NavLink key={setting.name} to={setting.path}>
                    <MenuItem>{setting.name}</MenuItem>
                  </NavLink>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    );
  });
};
export default NavigationBar;
